import { useEffectEvent, useLayoutEffect, useRef } from 'react';
import type {
  CellKeyboardEvent,
  CellRendererProps,
  EditCellKeyDownArgs,
  Maybe,
  Omit,
  RenderEditCellProps,
} from './types';
import { createCellEvent, getCellClassname, getCellStyle, onEditorNavigation } from './utils/grid-utils';

const canUsePostTask = typeof scheduler !== 'undefined' && typeof scheduler.postTask === 'function';

const cellEditingClassname = '!p-0 [&>input]:border-0 [&>input]:shadow-none [&>input]:bg-transparent';

type SharedCellRendererProps<R, SR> = Pick<CellRendererProps<R, SR>, 'colSpan'>;

interface EditCellProps<R, SR>
  extends Omit<RenderEditCellProps<R, SR>, 'onRowChange' | 'onClose'>,
    SharedCellRendererProps<R, SR> {
  rowIdx: number;
  onRowChange: (row: R, commitChanges: boolean, shouldFocusCell: boolean) => void;
  closeEditor: (shouldFocusCell: boolean) => void;
  navigate: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onKeyDown: Maybe<(args: EditCellKeyDownArgs<R, SR>, event: CellKeyboardEvent) => void>;
}

/**
 * Commits outside mousedown events after capture reaches portals and before blur unmounts the editor.
 * A scheduled fallback handles events whose propagation stops before returning to window.
 */
export function EditCell<R, SR>({
  column,
  colSpan,
  row,
  rowIdx,
  onRowChange,
  closeEditor,
  onKeyDown,
  navigate,
}: EditCellProps<R, SR>) {
  const captureEventRef = useRef<MouseEvent | undefined>(undefined);
  const abortControllerRef = useRef<AbortController>(undefined);
  const frameRequestRef = useRef<number>(undefined);
  const commitOnOutsideClick = column.editorOptions?.commitOnOutsideClick ?? true;

  // useEffectEvent keeps the useLayoutEffect from tearing down between renders, which would drop valid mousedowns.
  const commitOnOutsideMouseDown = useEffectEvent(() => {
    onClose(true, false);
  });

  function cancelTask() {
    captureEventRef.current = undefined;
    if (abortControllerRef.current !== undefined) {
      abortControllerRef.current.abort();
      abortControllerRef.current = undefined;
    }
    if (frameRequestRef.current !== undefined) {
      cancelAnimationFrame(frameRequestRef.current);
      frameRequestRef.current = undefined;
    }
  }

  useLayoutEffect(() => {
    if (!commitOnOutsideClick) return;

    function onWindowCaptureMouseDown(event: MouseEvent) {
      captureEventRef.current = event;

      if (canUsePostTask) {
        const abortController = new AbortController();
        const { signal } = abortController;
        abortControllerRef.current = abortController;
        // postTask runs the handler outside a React render and before the next paint.
        scheduler
          .postTask(commitOnOutsideMouseDown, { priority: 'user-blocking', signal })
          // ignore abort errors
          .catch(() => {});
      } else {
        frameRequestRef.current = requestAnimationFrame(commitOnOutsideMouseDown);
      }
    }

    function onWindowMouseDown(event: MouseEvent) {
      if (captureEventRef.current === event) {
        commitOnOutsideMouseDown();
      }
    }

    addEventListener('mousedown', onWindowCaptureMouseDown, { capture: true });
    addEventListener('mousedown', onWindowMouseDown);

    return () => {
      removeEventListener('mousedown', onWindowCaptureMouseDown, { capture: true });
      removeEventListener('mousedown', onWindowMouseDown);
      cancelTask();
    };
  }, [commitOnOutsideClick]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (onKeyDown) {
      const cellEvent = createCellEvent(event);
      onKeyDown(
        {
          mode: 'EDIT',
          row,
          column,
          rowIdx,
          navigate() {
            navigate(event);
          },
          onClose,
        },
        cellEvent,
      );
      if (cellEvent.isGridDefaultPrevented()) return;
    }

    if (event.key === 'Escape') {
      onClose();
    } else if (event.key === 'Enter') {
      onClose(true);
    } else if (onEditorNavigation(event)) {
      navigate(event);
    }
  }

  function onClose(commitChanges = false, shouldFocusCell = true) {
    if (commitChanges) {
      onRowChange(row, true, shouldFocusCell);
    } else {
      closeEditor(shouldFocusCell);
    }
  }

  function onEditorRowChange(row: R, commitChangesAndFocus = false) {
    onRowChange(row, commitChangesAndFocus, commitChangesAndFocus);
  }

  const { cellClass } = column;
  const className = getCellClassname(
    column,
    'rdg-editor-container',
    !column.editorOptions?.displayCellContent && cellEditingClassname,
    typeof cellClass === 'function' ? cellClass(row) : cellClass,
  );

  return (
    <div
      role="gridcell"
      aria-colindex={column.idx + 1} // aria-colindex is 1-based
      aria-colspan={colSpan}
      aria-selected
      className={className}
      style={getCellStyle(column, colSpan)}
      onKeyDown={handleKeyDown}
      onMouseDownCapture={cancelTask}
    >
      {column.renderEditCell != null && (
        <>
          {column.renderEditCell({
            column,
            row,
            rowIdx,
            onRowChange: onEditorRowChange,
            onClose,
          })}
          {column.editorOptions?.displayCellContent &&
            column.renderCell({
              column,
              row,
              rowIdx,
              isCellEditable: true,
              tabIndex: -1,
              onRowChange: onEditorRowChange,
            })}
        </>
      )}
    </div>
  );
}
